import { getAddress, getContract } from '@/utils/ether';
import { useWallet } from '@/hooks/wallet';
import { useRecoilValue } from 'recoil';
import { appConfigAtom } from '@/state/app';
import { getNetworkByChainId, getVocabByToken } from '@/utils/utils';
import { walletAtom } from '@/state/wallet';
import { BigNumber, ContractTransaction, ethers } from 'ethers';
import { Contract, ContractInstance } from '@/constants/types';
import { handleError } from '@/utils/error';
import { MAINNET } from '@/constants/env';

export interface ActionParams {
    amount?: BigNumber;
    address?: string;
    orderId?: number;
    tokenName?: Contract;
    contractName?: Contract;
}

export const useActions = () => {
    const { signer } = useWallet();
    const appConfig = useRecoilValue(appConfigAtom);
    const walletState = useRecoilValue(walletAtom);
    const tokenVocab = getVocabByToken(appConfig.token);
    const network = walletState.chainId ? getNetworkByChainId(walletState.chainId)?._id : null;
    const baseInfo = { token: appConfig.token, network, provider: signer } as ContractInstance;

    const ClayMain = () => getContract({ ...baseInfo, contractName: 'ClayMain' });
    const ClayExchangeV1 = () => getContract({ ...baseInfo, contractName: 'ClayExchangeV1' });

    // Token
    const approve = async ({ tokenName, contractName }: ActionParams) => {
        if (!tokenName || !contractName || (tokenVocab.isNative && tokenName == 'Token')) {
            return null;
        } else {
            const tokenContract = await getContract({ ...baseInfo, contractName: tokenName });
            const address = getAddress({ ...baseInfo, contractName });
            if (address && tokenContract) {
                return tokenContract.approve(address, ethers.constants.MaxUint256).catch(handleError());
            } else {
                return null;
            }
        }
    };

    // ClayMain Methods
    const stake = async ({ amount }: ActionParams) => {
        const contract = await ClayMain();
        if (appConfig.token === 'CSMATIC_POLYGON') {
            return contract?.depositETH({ value: amount! }).catch(handleError());
        } else if (appConfig.token === 'CSETH') {
            return contract?.deposit({ value: amount! }).catch(handleError());
        } else {
            return contract?.deposit(amount!).catch(handleError());
        }
    };
    const withdraw = async ({ amount }: ActionParams) => {
        const options = appConfig.token == 'CSMATIC' ? { gasLimit: MAINNET ? 6e5 : 1.4e6 } : {};
        const contract = await ClayMain();
        return contract?.withdraw(amount!, options).catch(handleError());
    };
    const instantWithdraw = async ({ amount }: ActionParams) => {
        const contract = appConfig.token === 'CSMATIC_POLYGON' ? await ClayExchangeV1() : await ClayMain();
        const withdrawTx = await contract?.instantWithdraw(amount!).catch(handleError());
        return withdrawTx;
    };
    const claim = async ({ orderId }: ActionParams) => {
        const contract = await ClayMain();
        return contract?.claim([orderId]).catch(handleError());
    };
    const earlyClaim = async ({ orderId }: ActionParams) => {
        const contract = await ClayMain();
        return await contract?.earlyClaim(orderId).catch(handleError());
    };

    // MATIC Polygon
    const swapCsTokenV2 = async () => {
        const contract = await ClayMain();
        return await contract?.swapTokens().catch(handleError());
    };

    // Other
    const txSuccess = async (txHash: ContractTransaction) => {
        try {
            const WAIT_BLOCKS = 1;
            // TODO this method takes longer than our tx manager, so locked forms wait too long
            // find alternative, maybe using tx manager here
            const receipt = await txHash.wait(WAIT_BLOCKS);
            return receipt.status == 1;
        } catch (e) {
            return false;
        }
    };

    return {
        approve,
        stake,
        withdraw,
        instantWithdraw,
        claim,
        earlyClaim,
        txSuccess,
        swapCsTokenV2,
    };
};
